<template>
    <div class="d-flex flex-column modal-inner p-4 max-w-xxl">
        <div>
            <div class="row mx-4 pb-1">
                <h3 class="m-0">Restrict to Users</h3>
            </div>
            <div class="row mx-4 pb-1 mt-1">
                <vuetable v-if="assignedUsersLoaded"
                    ref="vuetable"
                    :api-url="grid"
                    :fields="fields"
                    :css="css.table"
                    :per-page="perPage"
                    :append-params="moreParams"
                    noDataTemplate="No Users Found"
                    pagination-path="pagination"
                    @vuetable:pagination-data="onPaginationData"
                    @vuetable:loaded="onLoaded"
                    @vuetable:checkbox-toggled="onCheckbox"
                    @vuetable:checkbox-toggled-all="onCheckboxAll"
                >
                    <template slot="name" slot-scope="props">
                        {{ props.rowData.name }}
                    </template>
                    <template slot="roles" slot-scope="props">
                        <div>
                            <a class="badge badge-primary"
                                v-for="role in props.rowData.roles"
                                :href="'/roles/' + role.id +'/edit/'"
                                :key="role.id"
                            >
                                {{ role.display_name }}
                            </a>
                        </div>
                    </template>
                    <template slot="assigned" slot-scope="props">
                        <div>
                            <span v-if="props.rowData.assigned" class="badge badge-success">
                                Yes
                            </span>
                            <span v-else class="badge badge-danger">
                                No
                            </span>
                        </div>
                    </template>
                    <template slot="actions" slot-scope="props">
                        <div class="btn-group">
                            <a class="btn btn-sm btn-outline-secondary" :href="`/users/${props.rowData.id}/edit`">
                                <i class="fa fa-eye"></i>
                            </a>
                            <button v-if="isExistingWorkflow && props.rowData.assigned" class="btn btn-sm btn-danger" @click="handleDetachUser(props.rowData.id)" :disabled="loading">
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </template>
                </vuetable>
                <div class="row d-flex justify-content-between align-items-center w-100 m-auto pb-2">
                    <span>
                        <base-pagination-info
                            :css="css.table"
                            ref="paginationInfo"
                        ></base-pagination-info>
                    </span>
                    <span>
                        <base-pagination
                            ref="pagination"
                            @vuetable-pagination:change-page="onChangePage"
                        ></base-pagination>
                    </span>
                </div>
            </div>
            <div class="row mx-4 my-0 pb-0" v-if="workflowId !== ''">
                <div class="d-flex align-items-center">
                    <button v-if="isExistingWorkflow && selectedUnassignedUsers.length"
                        class="btn btn-sm btn-primary mr-1"
                        @click="addUsersToRestrict"
                        :disabled="loading"
                    >
                        Add Selected Unassigned User(s)
                    </button>
                    <button v-if="isExistingWorkflow && selectedAssignedUsers.length"
                        class="btn btn-sm btn-danger"
                        @click="detachAssignedUsers"
                        :disabled="loading"
                    >
                        Remove Selected Assigned User(s)
                    </button>
                </div>
            </div>
        </div>
        <div class="mt-4">
            <div class="row mx-4 pb-1">
                <h3 class="m-0">Limit by Role</h3>
            </div>
            <div class="row mx-4 my-1 pb-0" v-if="!loading">
                <label class="col-md-3 col-form-label font-weight-bold px-0">Assignable Roles</label>
            </div>
            <div class="row mx-4 pb-2 mt-1">
                <div class="max-w-md d-flex align-items-center" v-if="availableWorkflowRoles.length">
                    <select name="" id="" class="form-control" @change="handleSelectRole" :disabled="!availableWorkflowRoles.length || loading">
                        <option v-for="role in availableWorkflowRoles" :value="role.id" :key="role.id">
                            {{ role.name }}
                        </option>
                        <option value="" v-if="availableWorkflowRoles.length === 0">
                            No roles available
                        </option>
                    </select>
                    <button class="btn btn-primary ml-1" @click="handleAddRole" :disabled="!availableWorkflowRoles.length || loading || pendingWorkflowRoleIds.includes(selectedRole.id)">
                        Add Role
                    </button>
                </div>
            </div>
            <div class="row mx-4 my-1 pb-0" v-if="!loading">
                <label class="col-md-3 col-form-label font-weight-bold px-0">Attached/Pending Roles</label>
            </div>
            <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                <div v-if="isExistingWorkflow" class="d-flex flex-column max-w-md">
                    <div v-for="role in assignedWorkflowRoles" :key="role.id" class="d-flex w-100 align-items-center justify-content-between mb-2">
                        <span>{{ role.name }}</span>
                        <button class="btn btn-sm btn-danger" @click="handleDetachRole(role.id)" :disabled="loading">
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                    <div v-if="!pendingWorkflowRoles.length">No Roles Attached</div>
                </div>
                <div v-if="!isExistingWorkflow" class="d-flex flex-column max-w-md">
                    <div v-for="role in pendingWorkflowRoles" :key="role.id" class="d-flex w-100 align-items-center justify-content-between mb-2">
                        <span>{{ role.name }}</span>
                        <button class="btn btn-sm btn-danger" @click="handleDetachRole(role.id)" :disabled="loading">
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                    <div v-if="!pendingWorkflowRoles.length">No Roles Pending</div>
                </div>
                <div class="d-flex align-items-center mt-2">
                    <button class="btn my-2 mr-2 btn-outline-secondary" @click="handleClose">
                        Cancel
                    </button>
                    <button v-if="!isExistingWorkflow" class="btn btn-primary" @click="handleSave">
                        Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import axios from "axios";
import BasePagination from "../tables/base-pagination.vue";
import BasePaginationInfo from "../tables/base-pagination-info.vue";


const base_url = window.location.protocol + "//" + window.location.host;

export default {
    name: "workflow-restrict-user",
    components: {
        "base-pagination": BasePagination,
        "base-pagination-info": BasePaginationInfo,
    },
    props: {
        workflowId: {
            type: [Number, String],
            required: false
        },
        pendingUsers: {
            type: [Array, Object],
            required: false
        },
        pendingRoles: {
            type: [Array, Object],
            required: false
        },
    },
    mounted() {
        this.getAvailableRoles();

        if (this.isExistingWorkflow) {
            const users = `${base_url}/pimapi/workflows/${this.workflowId}/users`;
            const roles = `${base_url}/pimapi/workflows/${this.workflowId}/roles`;

            const getUsers = axios.get(users);
            const getRoles = axios.get(roles);

            axios.all([getUsers, getRoles])
                .then(axios.spread((...response) => {
                    const assignedUsers = response[0].data.payload;
                    const assignedRoles = response[1].data.payload;

                    const assignedRolesIds = [];

                    const assignedRolesFormatted = assignedRoles.reduce((a, c) => {
                        if (!assignedRolesIds.includes(c.id)) {
                            assignedRolesIds.push(c.id);
                            c.name = c.display_name;
                            a.push(c);
                        }

                        return a;
                    }, []);

                    this.assignedUsers = assignedUsers.map((user) => user.id);
                    this.assignedUsersLoaded = true;

                    this.assignedWorkflowRoles = assignedRolesFormatted;

                    this.fields.splice(this.fields.length - 1, 0, {
                        name: "__slot:assigned",
                        title: "Assigned",
                    });

                    this.$nextTick(() => {
                        this.$refs.vuetable.normalizeFields();
                    });
                }))
                .catch((errors) => {
                    console.log(errors);
                });
        } else {
            this.assignedUsersLoaded = true;
        }
    },
    data() {
        return {
            assignedUsers: [],
            assignedUsersLoaded: false,
            assignedWorkflowRoles: [],
            availableWorkflowRoles: [],
            pendingWorkflowRoles: [],
            selectedWorkflowUsers: this.pendingUsers.length ? this.pendingUsers : [],
            selectedRole: {
                id: null,
                name: ''
            },
            pendingWorkflowUsers: [],
            perPage: 4,
            moreParams: {sort: "firstname|asc"},
            loading: true,
            fields: [
                {
                    name: "__checkbox",
                    titleClass: 'center aligned',
                    dataClass: 'right aligned'
                },
                {
                    name: "__slot:name",
                    sortField: "firstname",
                    title: "Full Name",
                },
                {
                    name: "email",
                    title: "Email Address",
                },
                {
                    name: "__slot:roles",
                    title: "Roles",
                },
                {
                    name: "__slot:actions",
                    titleClass: "actions-single-column",
                },
            ],
            grid: base_url + "/users/grid",
            css: {
                table: {
                    tableClass:
                        "table table-striped table-bordered table-middle",
                    ascendingIcon: "sort-icon-asc",
                    descendingIcon: "sort-icon-desc",
                    infoClass: "",
                },
            },
        }
    },
    computed: {
        isExistingWorkflow() {
            return this.workflowId !== "" && !isNaN(parseInt(this.workflowId));
        },
        selectedProjectUserIds() {
            return this.selectedWorkflowUsers.map((user) => user.id);
        },
        selectedAssignedUsers() {
            return this.selectedWorkflowUsers.filter((user) => user.assigned).map((user) => user.id);
        },
        selectedUnassignedUsers() {
            return this.selectedWorkflowUsers.filter((user) => !user.assigned).map((user) => user.id);
        },
        pendingWorkflowRoleIds() {
            return this.pendingWorkflowRoles.map((role) => role.id);
        },
        assignedWorkflowRoleIds() {
            return this.assignedWorkflowRoles.map((role) => role.id);
        },
    },
    watch: {
        selectedWorkflowUsers: function() {
            this.pendingWorkflowUsers = this.selectedWorkflowUsers;
        },
        availableWorkflowRoles: function() {
            if (this.availableWorkflowRoles.length === 0) {
                this.selectedRole = { name: '', id: null }
            } else {
                this.selectedRole = this.availableWorkflowRoles[0];
            }
        },
    },
    methods: {
        async getAvailableRoles() {
            if (this.isExistingWorkflow) {
                const getAllRoles = axios.get(`${base_url}/pimapi/roles`);
                const getWorkflowRoles = axios.get(`${base_url}/pimapi/workflows/${this.workflowId}/roles`);

                axios.all([getAllRoles, getWorkflowRoles])
                    .then(axios.spread((...response) => {
                        const allRoles = response[0].data.roles;
                        const workflowRoles = response[1].data.payload;

                        const workflowRoleIds = workflowRoles.map((role) => role.id);
                        const workflowRolesFormatted = workflowRoles.reduce((a, c) => {
                            c.name = c.display_name;
                            a.push(c);

                            return a;
                        }, []);

                        this.availableWorkflowRoles = allRoles.filter((role) => !workflowRoleIds.includes(role.id));
                        this.assignedWorkflowRoles = workflowRolesFormatted;
                    }));
            } else {
                axios(base_url + '/pimapi/roles')
                    .then((response) => {
                        this.availableWorkflowRoles = response.data.roles;
                    })
                    .catch((err) => {
                        console.log(err)
                    });
            }
        },  
        async getAssignedUsers() {
            await axios(base_url + '/pimapi/workflows/' + this.workflowId + '/users')
                .then((response) => {
                    this.assignedUsers = response.data.payload.map((user) => user.id);
                    this.assignedUsersLoaded = true;
                })
                .catch((err) => {
                    console.log(err)
                })
        },
        preselectCheckboxes() {
            if (!this.isExistingWorkflow) {
                if (this.pendingUsers.length) {
                    this.$refs.vuetable.selectedTo = this.pendingUsers.map((user) => user.id);
                }

                if (this.pendingRoles.length) {
                    this.pendingWorkflowRoles = this.pendingRoles;
                }
            }
        },
        onPaginationData(paginationData) {
            this.$refs.pagination.setPaginationData(paginationData);
            this.$refs.paginationInfo.setPaginationData(paginationData);
            this.preselectCheckboxes();
        },
        onChangePage(page) {
            this.$refs.vuetable.changePage(page);
            this.preselectCheckboxes();
        },
        onLoaded: function () {
            this.loading = false;

            this.preselectCheckboxes();
        },
        onCheckbox(isChecked, row) {
            if (isChecked) {
                this.selectedWorkflowUsers.push(row);
            } else {
                this.selectedWorkflowUsers =
                    this.selectedWorkflowUsers.filter((pendingUser) => pendingUser.id !== row.id);
            }
        },
        onCheckboxAll(isChecked) {
            if (isChecked === false) {
                this.$refs.vuetable.selectedTo = [];
                this.selectedWorkflowUsers = [];
            } else {
                let selected = this.$refs.vuetable.tableData.map(item => item);
                this.selectedWorkflowUsers = this.selectedWorkflowUsers.concat(selected);
            }
        },
        handleClose() {
            this.$parent.restrictUserModal = false;
        },
        handleAlert(alert) {
            this.$parent.$parent.alert = alert;
        },
        handleSelectRole(e) {
            this.availableWorkflowRoles.forEach((role) => {
                if (role.id == e.target.value) {
                    this.selectedRole = role;
                }
            })
        },
        handleDetachRole(id) {
            if (this.isExistingWorkflow) {
                this.loading = true;

                axios.post(base_url + '/pimapi/workflows/' + this.workflowId + '/roles/detach', { roleIds: [id] })
                    .then(function(res) {
                        this.loading = false;

                        this.handleAlert({
                            message: 'User role removed',
                            status: "alert-success"
                        });

                        this.getAvailableRoles();
                        this.$refs.vuetable.refresh();
                    }.bind(this))
                    .catch(function(err) {
                        this.loading = false;

                        this.handleAlert({
                            message: 'An error occurred',
                            status: "alert-success"
                        });

                        window.setTimeout(() => {
                            window.location.reload();
                        }, 3500)
                    }.bind(this));
            } else {
                this.removePendingRole(id);
            }
        },
        handleAddRole() {
            if (this.isExistingWorkflow) {
                this.loading = true;

                axios.post(base_url + '/pimapi/workflows/' + this.workflowId + '/roles', {
                    roleIds: [this.selectedRole.id],
                })
                    .then(function(res) {
                        this.loading = false;

                        this.handleAlert({
                            message: 'User role added',
                            status: "alert-success"
                        });

                        this.selectedRole = {
                            id: null,
                            name: ''
                        };

                        this.getAvailableRoles();
                        this.$refs.vuetable.refresh();
                    }.bind(this))
                    .catch(function(err) {
                        this.loading = false;

                        this.handleAlert({
                            message: 'An error occurred',
                            status: "alert-success"
                        });

                        // this.$nextTick(() => {
                        //     window.location.reload();
                        // });
                    }.bind(this));
            } else {
                this.addPendingRole()
            }
        },
        addPendingRole() {
            this.pendingWorkflowRoles.push(this.selectedRole);
            this.availableWorkflowRoles = this.availableWorkflowRoles.filter((role) => role.id !== this.selectedRole.id);
        },
        removePendingRole(id) {
            let currentRole = this.pendingWorkflowRoles.find((pendingRole) => pendingRole.id == id);
            this.availableWorkflowRoles.push(currentRole);
            this.pendingWorkflowRoles = this.pendingWorkflowRoles.filter((pendingRole) => pendingRole.id != id);
        },
        async addUsersToRestrict() {
            this.loading = true;

            await axios
                .post("/pimapi/workflows/" + this.workflowId + "/users", {
                    userIds: this.selectedUnassignedUsers
                })
                .then((response) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-success",
                        message: 'User(s) added',
                    });
                })
                .catch((err) => {
                    console.log(err);

                    this.loading = false;

                    this.handleAlert({
                        status: "alert-danger",
                        message: err.response.data.message,
                    });
                })

            await this.getAssignedUsers();
            this.clearTable();
        },
        async detachAssignedUsers() {
            this.loading = true;

            await axios
                .post("/pimapi/workflows/" + this.workflowId + "/users/detach", {
                    userIds: this.selectedAssignedUsers
                })
                .then((response) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-success",
                        message: "User removed",
                    });
                })
                .catch((err) => {
                    console.log(err);

                    this.loading = false;

                    this.handleAlert({
                        status: "alert-danger",
                        message: err.response.data.message,
                    });
                })

            await this.getAssignedUsers();
            this.clearTable();
        },
        async handleDetachUser(id) {
            this.loading = true;

            axios
                .post("/pimapi/workflows/" + this.workflowId + "/users/detach", {
                    userIds: [id]
                })
                .then((response) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-success",
                        message: "User removed",
                    });

                    this.$refs.vuetable.refresh();
                })
                .catch((err) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-danger",
                        message: err.response.data.message,
                    });
                })

            await this.getAssignedUsers();
            this.clearTable();
        },
        clearTable() {
            this.selectedWorkflowUsers = [];
            this.$refs.vuetable.selectedTo = [];
            this.$refs.vuetable.refresh();
        },
        handleSave() {
            this.$parent.pendingWorkflowUsers = this.pendingWorkflowUsers;
            this.$parent.pendingWorkflowRoles = this.pendingWorkflowRoles;
            this.$parent.availableWorkflowRoles = this.availableWorkflowRoles;

            this.handleClose();
        },
        transform(data) {
            const transformed = {};

            transformed.pagination = {
                total: data.total,
                per_page: data.per_page,
                current_page: data.current_page,
                last_page: data.last_page,
                next_page_url: data.next_page_url,
                prev_page_url: data.prev_page_url,
                from: data.from,
                to: data.to
            }

            transformed.data = [];

            for (let i = 0; i < data.data.length; i++) {
                transformed.data.push({
                    id: data.data[i].id,
                    email: data.data[i].email,
                    name: data.data[i].name,
                    roles: data.data[i].roles,
                    assigned: this.assignedUsers.includes(data.data[i].id)
                });
            }

            transformed.data.sort((a, b) => a.name.localeCompare(b.name));

            return transformed
        },
    }
}
</script>

